// src/services/assignmentService.js
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
  updateDoc,
  query,
  serverTimestamp,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { convertToDate } from '../../../utils/dateUtils';

export const fetchAssignments = async () => {
  try {
    const snapshot = await getDocs(
      query(collection(db, 'assignments'), orderBy('order', 'asc'))
    );

    const assignments = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      assignments.push({
        id: doc.id,
        title: data.title || 'Untitled Assignment',
        instruction: data.instruction || '',
        startDate: convertToDate(data.startDate),
        endDate: convertToDate(data.endDate),
        targetGroups: data.targetGroups || [],
        groupSettings: data.groupSettings || {},
        order: data.order || 0,
        programId: data.programId || null,   // ← was missing
        createdAt: convertToDate(data.createdAt) || new Date(),
        updatedAt: convertToDate(data.updatedAt) || null
      });
    });

    return assignments;
  } catch (error) {
    console.error("Error fetching assignments:", error);
    throw error;
  }
};

export const getAssignmentStatus = (assignment) => {
  const { startDate, endDate } = assignment;
  const now = new Date();

  if (!startDate) return 'Draft';
  if (startDate > now) return 'Upcoming';
  if (!endDate || endDate > now) return 'Open';
  return 'Closed';
};

export const getAssignmentStatusForGroup = (assignment, groupId) => {
  const gs = assignment.groupSettings?.[groupId];
  const effectiveStart = gs?.startDate ? convertToDate(gs.startDate) : assignment.startDate;
  const effectiveEnd   = gs?.endDate   ? convertToDate(gs.endDate)   : assignment.endDate;
  const now = new Date();

  if (!effectiveStart) return 'Draft';
  if (effectiveStart > now) return 'Upcoming';
  if (!effectiveEnd || effectiveEnd > now) return 'Open';
  return 'Closed';
};

export const deleteAssignment = async (assignmentId) => {
  try {
    await deleteDoc(doc(db, 'assignments', assignmentId));
  } catch (error) {
    console.error("Error deleting assignment:", error);
    throw error;
  }
};

export const saveAssignment = async (assignmentData, assignmentId = null) => {
  try {
    const startDate = convertToDate(assignmentData.startDate);
    const endDate   = convertToDate(assignmentData.endDate);

    const formattedData = {
      ...assignmentData,
      startDate: startDate ? Timestamp.fromDate(startDate) : null,
      endDate:   endDate   ? Timestamp.fromDate(endDate)   : null,
      updatedAt: serverTimestamp()
    };

    // Status is derived from dates only — never stored
    delete formattedData.status;

    if (assignmentId) {
      await updateDoc(doc(db, 'assignments', assignmentId), formattedData);
      return assignmentId;
    } else {
      formattedData.createdAt = serverTimestamp();
      const docRef = await addDoc(collection(db, 'assignments'), formattedData);
      return docRef.id;
    }
  } catch (error) {
    console.error("Error saving assignment:", error);
    throw error;
  }
};

export const updateGroupSettings = async (assignmentId, groupId, settings) => {
  try {
    const assignmentRef = doc(db, 'assignments', assignmentId);

    if (settings.startDate) {
      const d = convertToDate(settings.startDate);
      settings.startDate = d ? Timestamp.fromDate(d) : null;
    }
    if (settings.endDate) {
      const d = convertToDate(settings.endDate);
      settings.endDate = d ? Timestamp.fromDate(d) : null;
    }
    delete settings.status;

    const snapshot = await getDocs(assignmentRef);
    const currentGroupSettings = snapshot.data()?.groupSettings || {};

    await updateDoc(assignmentRef, {
      groupSettings: {
        ...currentGroupSettings,
        [groupId]: { ...currentGroupSettings[groupId], ...settings }
      },
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error("Error updating group settings:", error);
    throw error;
  }
};

const assignmentService = {
  fetchAssignments,
  getAssignmentStatus,
  getAssignmentStatusForGroup,
  saveAssignment,
  deleteAssignment,
  updateGroupSettings
};

export default assignmentService;